import { router } from '@inertiajs/react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import WarningIcon from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DataGrid } from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';
import * as React from 'react';
import Copyright from '../../internals/components/Copyright';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('pt-BR');
};

const formatCNPJ = (cnpj) => {
  if (!cnpj) return '-';
  return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

const formatPhone = (phone) => {
  if (!phone) return '-';
  return phone.replace(/^(\d{2})(\d{4,5})(\d{4})$/, '($1) $2-$3');
};

const handleEdit = (edit_url) => {
  router.visit(edit_url);
};

export default function CarriersTable({ carriers = [] }) {
  const [deleteDialog, setDeleteDialog] = React.useState({
    open: false,
    carrierName: '',
    destroyUrl: '',
    loading: false
  });

  // Ensure we have an array and add unique IDs
  const rows = Array.isArray(carriers) ? carriers.map((carrier, index) => ({
    id: carrier.id || index,
    ...carrier,
  })) : [];

  const handleDeleteClick = (destroy_url, carrierName) => {
    setDeleteDialog({
      open: true,
      carrierName,
      destroyUrl: destroy_url,
      loading: false
    });
  };

  const handleDeleteConfirm = () => {
    setDeleteDialog(prev => ({ ...prev, loading: true }));

    router.delete(deleteDialog.destroyUrl, {
      preserveScroll: true,
      onSuccess: () => {
        setDeleteDialog({
          open: false,
          carrierName: '',
          destroyUrl: '',
          loading: false
        });
      },
      onError: (errors) => {
        setDeleteDialog(prev => ({ ...prev, loading: false }));
        // You could show an error message here
      }
    });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({
      open: false,
      carrierName: '',
      destroyUrl: '',
      loading: false
    });
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Nome',
      width: 250,
      flex: 1,
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 200,
      flex: 1,
    },
    {
      field: 'cnpj',
      headerName: 'CNPJ',
      width: 150,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {formatCNPJ(params.value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Telefone',
      width: 130,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {formatPhone(params.value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'drivers_count',
      headerName: 'Motoristas',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 0.5
        }}>
          <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2">
            {params.value || 0}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'geolocation',
      headerName: 'Geolocalização',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Chip
            label={params.value ? 'Ativa' : 'Inativa'}
            size="small"
            color={params.value ? 'success' : 'default'}
            icon={params.value ? <LocationOnIcon /> : undefined}
          />
        </Box>
      ),
    },
    {
      field: 'geolocation_interval',
      headerName: 'Intervalo (min)',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {params.value ? `${params.value} min` : '-'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Criado em',
      width: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            {formatDate(params.value)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Ações',
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      width: 120,
      renderCell: (params) => (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 0.5
        }}>
          <Tooltip title="Editar transportadora">
            <IconButton
              sx={{
                height: '30px',
                width: '35px',
              }}
              size="small"
              onClick={() => handleEdit(params.row.edit_url)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir transportadora">
            <IconButton
              sx={{
                height: '30px',
                width: '35px',
              }}
              size="small"
              color="error"
              onClick={() => handleDeleteClick(params.row.destroy_url, params.row.name)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        Transportadoras
      </Typography>

      <DataGrid
        checkboxSelection
        disableRowSelectionOnClick
        rows={rows}
        columns={columns}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        initialState={{
          pagination: { paginationModel: { pageSize: 20 } },
        }}
        pageSizeOptions={[10, 20, 50]}
        disableColumnResize
        density="compact"
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        slotProps={{
          filterPanel: {
            filterFormProps: {
              logicOperatorInputProps: {
                variant: 'outlined',
                size: 'small',
              },
              columnInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              operatorInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              valueInputProps: {
                InputComponentProps: {
                  variant: 'outlined',
                  size: 'small',
                },
              },
            },
          },
        }}

      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Confirmar Exclusão
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Tem certeza que deseja excluir a transportadora{' '}
            <strong>{deleteDialog.carrierName}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'warning.main' }}>
            Esta ação não pode ser desfeita e todos os dados relacionados serão perdidos.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteDialog.loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="warning"
            variant="contained"
            disabled={deleteDialog.loading}
          >
            {deleteDialog.loading ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      <Copyright sx={{ my: 4 }} />
    </Box>
  );
}